import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Badge, Box, FormControlLabel, IconButton, ListItemButton, ListItemText, Popover, Switch } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

export default function SettingsButton({ useClientAisles, setUseClientAisles, useRealBbox, setUseRealBbox, metadataUrl, downloadOptions }) {
  const [openSettings, setOpenSettings] = useState(null);
  const [openDownload, setOpenDownload] = useState(null);
  const { t } = useTranslation();

  const buttonRef = useRef(null);
  const toggleUseClientAisles = useCallback(() => {
    setUseClientAisles(prev => !prev);
  }, [setUseClientAisles]);

  const toggleUseRealBbox = useCallback(() => {
    setUseRealBbox(prev => !prev);
  }, [setUseRealBbox]);

  useEffect(() => {
    if (buttonRef?.current && !openSettings) {
      buttonRef.current.blur();
    }
  }, [openSettings]);

  const isAnyActive = useMemo(() => (useClientAisles || useRealBbox), [useClientAisles, useRealBbox]);
  return (
    <>
      <IconButton sx={{ position: 'absolute', bottom: '10px', left: '50px', zIndex: 1000 }} onClick={(e) => setOpenDownload(e.currentTarget)} id="map-download-button">
        <IconComponent iconName="download" />
      </IconButton>
      {/* Settings Button */}
      <IconButton sx={{ position: 'absolute', bottom: '10px', left: '10px', zIndex: 1000 }} onClick={(e) => setOpenSettings(e.currentTarget)} id="map-settings-button" ref={buttonRef}>
        {isAnyActive ?
          <Badge variant="dot" color="primary">
            <IconComponent iconName="settings" />
          </Badge>
          :
          <IconComponent iconName="settings" />}
      </IconButton>

      {/* Download Options Popover */}
      <Popover
        open={!!openDownload}
        onClose={() => setOpenDownload(null)}
        anchorEl={openDownload}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', minWidth: 200 }}>
          <ListItemButton onClick={() => { downloadOptions.rawMap(); setOpenDownload(null); }}>
            <ListItemText
              primary={t('overseer_app.layouts.download.rawMap', 'Raw Map')}
            />
          </ListItemButton>
          <ListItemButton onClick={() => { downloadOptions.displayedLayout(); setOpenDownload(null); }}>
            <ListItemText
              primary={t('overseer_app.layouts.download.displayedLayout', 'Displayed Layout')}
            />
          </ListItemButton>
          <ListItemButton
            component="a"
            href={metadataUrl || undefined}
            download
            disabled={!metadataUrl}
            sx={{ opacity: metadataUrl ? 1 : 0.5 }}
          >
            <ListItemText
              primary={!metadataUrl
                ? t('overseer_app.layouts.download.loading', 'Loading...')
                : t('overseer_app.layouts.download.mapMetadata', 'Map Metadata')
              }
            />
          </ListItemButton>

        </Box>
      </Popover>

      {/* Settings Popover */}
      <Popover
        open={!!openSettings}
        onClose={() => setOpenSettings(null)}
        anchorEl={openSettings}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel checked={useClientAisles} onChange={toggleUseClientAisles} control={<Switch size='small' />} label="Aisle names by client" sx={{ fontSize: '10px' }} />
          <FormControlLabel checked={useRealBbox} onChange={toggleUseRealBbox} control={<Switch size='small' />} label="Use real bbox" sx={{ fontSize: '10px' }} />
        </Box>
      </Popover>
    </>
  );
}
